import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { Clock, Plus, Send, ShieldCheck, ShieldOff } from 'lucide-react'

const STATUS_COLORS: Record<string, string> = {
  open:        'bg-blue-100 text-blue-700',
  in_progress: 'bg-violet-100 text-violet-700',
  resolved:    'bg-green-100 text-green-700',
  closed:      'bg-gray-100 text-gray-500',
}

export function InstitutionSupportView() {
  const { orgId, can, roleName } = useOrgPermissions()
  const { toast } = useToast()
  const qc = useQueryClient()

  const [selectedTicket, setSelectedTicket] = useState<any | null>(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [grantOpen, setGrantOpen] = useState(false)
  const [granting, setGranting] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const channelRef = useRef<any>(null)

  // New ticket sheet state
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', priority: 'medium' })
  const [creating, setCreating] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Admins and owners see all org tickets; regular members see only their own.
  const canViewAllOrgTickets = roleName === 'owner' || roleName === 'admin' || can('organogram', 'full')

  const { data: tickets } = useQuery({
    queryKey: ['institution-tickets', orgId, canViewAllOrgTickets],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      // CCOs (support:full) see all org tickets; regular members see only their own
      const filter = orgId && canViewAllOrgTickets
        ? `org_id.eq.${orgId},submitted_by.eq.${user.id}`
        : `submitted_by.eq.${user.id}`
      const { data } = await (supabase
        .from('support_tickets' as any)
        .select('*')
        .or(filter)
        .order('created_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    }
  })

  const { data: messages } = useQuery({
    queryKey: ['institution-ticket-messages', selectedTicket?.id],
    enabled: !!selectedTicket,
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_ticket_messages' as any)
        .select('*')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at') as any)
      return (data ?? []) as any[]
    }
  })

  const { data: accessSession } = useQuery({
    queryKey: ['institution-access-session', selectedTicket?.id],
    enabled: !!selectedTicket,
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_access_sessions' as any)
        .select('*, agent:agent_id(first_name, last_name)')
        .eq('ticket_id', selectedTicket.id)
        .eq('status', 'active')
        .maybeSingle() as any)
      return data as any
    }
  })

  const { data: accessLog } = useQuery({
    queryKey: ['institution-access-log', selectedTicket?.id],
    enabled: !!selectedTicket && !!accessSession,
    queryFn: async () => {
      const { data } = await (supabase
        .from('support_access_log' as any)
        .select('*')
        .eq('session_id', accessSession?.id)
        .order('created_at', { ascending: false })
        .limit(50) as any)
      return (data ?? []) as any[]
    }
  })

  // Realtime access log
  useEffect(() => {
    if (!accessSession?.id) return
    const channel = supabase
      .channel(`institution-access-log-${accessSession.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'support_access_log',
        filter: `session_id=eq.${accessSession.id}`,
      }, () => {
        qc.invalidateQueries({ queryKey: ['institution-access-log', selectedTicket?.id] })
      })
      .subscribe()
    channelRef.current = channel
    return () => { channel.unsubscribe() }
  }, [accessSession?.id, selectedTicket?.id, qc])

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !orgId) return
    setCreating(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: ticket } = await (supabase
        .from('support_tickets' as any)
        .insert({
          org_id: orgId,
          title: newTicket.subject.trim(),
          description: newTicket.description.trim(),
          priority: newTicket.priority,
          status: 'open',
          submitted_by: user?.id,
        })
        .select()
        .single() as any)
      toast({ title: 'Ticket created.' })
      setNewTicket({ subject: '', description: '', priority: 'medium' })
      setSheetOpen(false)
      qc.invalidateQueries({ queryKey: ['institution-tickets', orgId] })
      if (ticket) setSelectedTicket(ticket)
    } catch {
      toast({ title: 'Failed to create ticket.', variant: 'destructive' })
    } finally {
      setCreating(false)
    }
  }

  const sendReply = async () => {
    if (!reply.trim() || !selectedTicket) return
    setSending(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await (supabase
        .from('support_ticket_messages' as any)
        .insert({
          ticket_id: selectedTicket.id,
          sender_id: user?.id,
          body: reply.trim(),
          sender_type: 'org',
        }) as any)
      setReply('')
      qc.invalidateQueries({ queryKey: ['institution-ticket-messages', selectedTicket.id] })
    } catch {
      toast({ title: 'Failed to send reply.', variant: 'destructive' })
    } finally {
      setSending(false)
    }
  }

  const grantAccess = async () => {
    if (!selectedTicket) return
    setGranting(true)
    try {
      await supabase.functions.invoke('grant-support-access', {
        body: { ticket_id: selectedTicket.id, org_id: orgId }
      })
      toast({ title: 'Support access granted.' })
      setGrantOpen(false)
      qc.invalidateQueries({ queryKey: ['institution-access-session', selectedTicket.id] })
    } catch {
      toast({ title: 'Failed to grant access.', variant: 'destructive' })
    } finally {
      setGranting(false)
    }
  }

  const revokeAccess = async () => {
    if (!accessSession) return
    setRevoking(true)
    try {
      await supabase.functions.invoke('revoke-support-access', {
        body: { session_id: accessSession.id }
      })
      toast({ title: 'Support access revoked.' })
      qc.invalidateQueries({ queryKey: ['institution-access-session', selectedTicket?.id] })
    } catch {
      toast({ title: 'Failed to revoke access.', variant: 'destructive' })
    } finally {
      setRevoking(false)
    }
  }

  const expiryCountdown = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now()
    if (diff <= 0) return 'Expired'
    const h = Math.floor(diff / 3600000)
    const m = Math.floor((diff % 3600000) / 60000)
    return `${h}h ${m}m`
  }

  const isCCO = can('organogram', 'full')

  return (
    <div className="flex gap-6 h-full">
      {/* Ticket list */}
      <div className="w-80 shrink-0 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Support</h2>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button size="sm" className="h-7 text-xs gap-1">
                <Plus className="w-3.5 h-3.5" />
                New Ticket
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-96">
              <SheetHeader>
                <SheetTitle>New Support Ticket</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label>Subject</Label>
                  <Input
                    value={newTicket.subject}
                    onChange={e => setNewTicket(t => ({ ...t, subject: e.target.value }))}
                    placeholder="Brief description of the issue…"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea
                    value={newTicket.description}
                    onChange={e => setNewTicket(t => ({ ...t, description: e.target.value }))}
                    placeholder="More details about the issue…"
                    rows={4}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={newTicket.priority} onValueChange={v => setNewTicket(t => ({ ...t, priority: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['low', 'medium', 'high', 'urgent'].map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="w-full"
                  onClick={createTicket}
                  disabled={creating || !newTicket.subject}
                >
                  {creating ? 'Creating…' : 'Submit Ticket'}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="space-y-2">
          {(tickets ?? []).map((ticket: any) => (
            <button
              key={ticket.id}
              onClick={() => setSelectedTicket(ticket)}
              className={cn(
                'w-full text-left p-3 rounded-lg border text-sm transition-colors',
                selectedTicket?.id === ticket.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:bg-muted/40'
              )}
            >
              <div className="font-medium truncate">{ticket.title}</div>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={cn('text-[10px]', STATUS_COLORS[ticket.status] ?? STATUS_COLORS.open)}>
                  {ticket.status}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(ticket.created_at).toLocaleDateString()}
                </span>
              </div>
            </button>
          ))}
          {(tickets ?? []).length === 0 && (
            <p className="text-justify text-sm text-muted-foreground text-center py-8">No tickets yet.</p>
          )}
        </div>
      </div>

      {/* Ticket detail */}
      {selectedTicket ? (
        <div className="flex-1 space-y-4 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold flex-1 truncate">{selectedTicket.title}</h3>
            <Badge className={STATUS_COLORS[selectedTicket.status] ?? STATUS_COLORS.open}>
              {selectedTicket.status}
            </Badge>
          </div>

          {/* Active session banner */}
          {accessSession ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-800 text-sm font-medium">
                  <Clock className="w-4 h-4" />
                  Support access active: {accessSession.agent?.first_name} {accessSession.agent?.last_name}
                  {' '}expires in {accessSession.expires_at ? expiryCountdown(accessSession.expires_at) : ''}
                </div>
                {isCCO && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={revokeAccess}
                    disabled={revoking}
                  >
                    <ShieldOff className="w-3 h-3 mr-1" />
                    {revoking ? 'Revoking…' : 'Revoke Access'}
                  </Button>
                )}
              </div>
              {(accessLog ?? []).length > 0 && (
                <div className="border-t border-green-200 pt-2 space-y-1 max-h-32 overflow-y-auto">
                  {(accessLog ?? []).map((log: any) => (
                    <div key={log.id} className="text-[11px] text-green-800 flex items-start gap-2">
                      <span className="text-green-500 shrink-0">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </span>
                      <span>{log.action}: {log.affected_resource}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : isCCO ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-sm"
              onClick={() => setGrantOpen(true)}
            >
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              Grant Support Access
            </Button>
          ) : null}

          {/* Message thread */}
          <div className="flex-1 space-y-3 max-h-96 overflow-y-auto">
            {(messages ?? []).map((msg: any) => (
              <div
                key={msg.id}
                className={cn('flex', msg.sender_type === 'org' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[80%] rounded-xl px-4 py-2.5 text-sm',
                    msg.sender_type === 'org'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  )}
                >
                  <p>{msg.body}</p>
                  <p className={cn(
                    'text-[10px] mt-1',
                    msg.sender_type === 'org' ? 'text-primary-foreground/60' : 'text-muted-foreground'
                  )}>
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {(messages ?? []).length === 0 && (
              <p className="text-justify text-sm text-muted-foreground text-center py-8">No messages yet.</p>
            )}
          </div>

          {/* Reply */}
          <div className="flex gap-2">
            <Textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Type a reply… (Ctrl+Enter to send)"
              className="min-h-[80px] text-sm resize-none"
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply()
              }}
            />
            <Button
              className="self-end shrink-0"
              onClick={sendReply}
              disabled={sending || !reply.trim()}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          Select a ticket to view the conversation.
        </div>
      )}

      {/* Grant access dialog */}
      <AlertDialog open={grantOpen} onOpenChange={setGrantOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Grant Support Access</AlertDialogTitle>
            <AlertDialogDescription className="text-justify leading-relaxed">
              By granting support access, you authorise a named CONORBIT support agent to view
              and act within your organisation's data for a period of 24 hours. Every action
              the agent takes will be recorded and visible to you in real time below. You may
              revoke this access at any time.
              {accessSession && (
                <span className="block mt-2 font-medium">
                  Agent: {accessSession.agent?.first_name} {accessSession.agent?.last_name}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={grantAccess} disabled={granting}>
              {granting ? 'Granting…' : 'Confirm & Grant Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
